// Modal "Editar ficha de la empresa" del detalle de /admin/contracts: razón social, RUT,
// dirección fiscal y los contactos principal y de facturación. Manda solo lo que cambió
// (ver companyProfileForm) y, al guardar, el padre relee el contrato poblado.
//
// El RUT queda de solo lectura para quien no es admin del operador y en las empresas de un
// holding: contracts respondería 403 / 422, así que no se ofrece.
//
// El padre lo monta al abrirlo y lo desmonta al cerrarlo: el estado inicial sale del
// contrato de ese momento.
import { useId, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { updateCompanyProfile } from "../../../../api/company";
import { Button } from "../../../../components/ui/Button";
import { Input } from "../../../../components/ui/Input";
import { Modal } from "../../../../components/ui/Modal";
import { cn } from "../../../../utils/cn";
import { showToast } from "../../../../utils/showToast";
import { TAX_ID_LABEL } from "../../settings/sections/invoicing/fiscalCopy";
import {
  buildProfilePayload,
  profileDefaults,
  profileErrorKey,
  taxIdCountryOf,
  validateProfileForm,
} from "./companyProfileForm";

function Field({ label, htmlFor, error, errorId, hint, hintId, className, children }) {
  return (
    <div className={cn("flex flex-col gap-1", className)}>
      <label htmlFor={htmlFor} className="text-sm font-medium text-fg">
        {label}
      </label>
      {children}
      {hint && !error && (
        <span id={hintId} className="text-xs text-fg-muted">
          {hint}
        </span>
      )}
      {error && (
        <span id={errorId} className="text-xs text-status-error">
          {error}
        </span>
      )}
    </div>
  );
}

/**
 * @param {{ open: boolean, onClose: () => void, contract: object, canEditTaxId: boolean,
 *           onSaved: () => (void|Promise<void>) }} props
 */
export default function EditCompanyProfileModal({ open, onClose, contract, canEditTaxId, onSaved }) {
  const { t } = useTranslation();
  const baseId = useId();
  const id = (name) => `${baseId}-${name}`;

  const company = contract?.companyId || {};
  const taxIdCountry = taxIdCountryOf(company.operationsCountry);
  const taxIdLabel = TAX_ID_LABEL[taxIdCountry] ?? TAX_ID_LABEL.CL;
  const isHoldingChild = Boolean(company.holdingId);
  const taxIdLocked = !canEditTaxId || isHoldingChild;

  const initial = useMemo(() => profileDefaults(contract), [contract]);
  const [values, setValues] = useState(initial);
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState(null);
  const [saving, setSaving] = useState(false);

  const tr = (key) => (key ? t(key, { taxIdLabel }) : undefined);

  const setField = (name, value) => {
    setValues((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => (prev[name] ? { ...prev, [name]: undefined } : prev));
  };
  const setContactField = (which, name, value) => {
    setValues((prev) => ({ ...prev, [which]: { ...prev[which], [name]: value } }));
    const key = `${which}.${name}`;
    setErrors((prev) => (prev[key] ? { ...prev, [key]: undefined } : prev));
  };

  const close = () => {
    if (!saving) onClose();
  };

  const submit = async (event) => {
    event.preventDefault();
    setFormError(null);
    const nextErrors = validateProfileForm(values, initial, { taxIdCountry });
    setErrors(nextErrors);
    if (Object.values(nextErrors).some(Boolean)) return;

    const payload = buildProfilePayload(values, initial);
    if (!payload) {
      onClose();
      return;
    }
    setSaving(true);
    const result = await updateCompanyProfile(company._id, payload);
    setSaving(false);
    if (result?.error) {
      setFormError(profileErrorKey(result));
      return;
    }
    showToast(t("Ficha de la empresa actualizada"), true);
    await onSaved();
  };

  // Input de texto con label, error y aria enlazados.
  const textField = ({ name, label, value, onChange, error, type = "text", disabled, hint, className, autoComplete }) => {
    const errorId = id(`${name}-error`);
    const hintId = id(`${name}-hint`);
    return (
      <Field
        label={label}
        htmlFor={id(name)}
        error={error}
        errorId={errorId}
        hint={hint}
        hintId={hintId}
        className={className}
      >
        <Input
          id={id(name)}
          type={type}
          value={value}
          disabled={disabled || saving}
          autoComplete={autoComplete}
          onChange={(e) => onChange(e.target.value)}
          invalid={!!error}
          aria-invalid={!!error}
          aria-describedby={error ? errorId : hint ? hintId : undefined}
        />
      </Field>
    );
  };

  const contactFields = (which) => (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      {textField({
        name: `${which}-name`,
        label: t("Nombre"),
        value: values[which].name,
        onChange: (v) => setContactField(which, "name", v),
        error: tr(errors[`${which}.name`]),
        autoComplete: "off",
      })}
      {textField({
        name: `${which}-email`,
        label: t("Email"),
        type: "email",
        value: values[which].email,
        onChange: (v) => setContactField(which, "email", v),
        error: tr(errors[`${which}.email`]),
        autoComplete: "off",
      })}
      {textField({
        name: `${which}-phone`,
        label: t("Teléfono"),
        type: "tel",
        value: values[which].phone,
        onChange: (v) => setContactField(which, "phone", v),
        error: tr(errors[`${which}.phone`]),
        autoComplete: "off",
      })}
      {which === "billing" &&
        textField({
          name: "billing-rut",
          label: t("{{taxIdLabel}} del contacto de facturación", { taxIdLabel }),
          value: values.billing.rut,
          onChange: (v) => setContactField("billing", "rut", v),
          error: tr(errors["billing.rut"]),
          hint: t("Opcional: a quién se emite el documento."),
          autoComplete: "off",
        })}
    </div>
  );

  const formId = id("form");
  return (
    <Modal
      open={open}
      onClose={close}
      size="lg"
      title={t("Editar ficha de la empresa")}
      description={company.name || undefined}
      footer={
        <>
          <Button variant="ghost" onClick={close} disabled={saving}>
            {t("Cancelar")}
          </Button>
          <Button type="submit" form={formId} disabled={saving}>
            {saving ? t("Guardando…") : t("Guardar cambios")}
          </Button>
        </>
      }
    >
      <form id={formId} onSubmit={submit} noValidate className="flex flex-col gap-5">
        <section className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {textField({
            name: "legalName",
            label: t("Razón social"),
            value: values.legalName,
            onChange: (v) => setField("legalName", v),
            error: tr(errors.legalName),
          })}
          {textField({
            name: "businessIdentifier",
            label: taxIdLabel,
            value: values.businessIdentifier,
            onChange: (v) => setField("businessIdentifier", v),
            error: tr(errors.businessIdentifier),
            disabled: taxIdLocked,
            hint: isHoldingChild
              ? t("El {{taxIdLabel}} de una empresa de un holding lo administra el holding.", { taxIdLabel })
              : !canEditTaxId
                ? t("Solo un administrador del operador puede cambiar el {{taxIdLabel}}.", { taxIdLabel })
                : undefined,
          })}
          {textField({
            name: "fiscalAddress",
            label: t("Dirección fiscal"),
            value: values.fiscalAddress,
            onChange: (v) => setField("fiscalAddress", v),
            error: tr(errors.fiscalAddress),
            className: "sm:col-span-2",
          })}
        </section>

        <section>
          <h3 className="text-[11px] uppercase tracking-wider font-semibold text-fg-muted mb-3">
            {t("Contacto principal")}
          </h3>
          {contactFields("primary")}
        </section>

        <section>
          <h3 className="text-[11px] uppercase tracking-wider font-semibold text-fg-muted mb-3">
            {t("Contacto de facturación")}
          </h3>
          {contactFields("billing")}
        </section>

        <p className="text-xs text-fg-muted">
          {t(
            "Al guardar un contacto, reemplaza a los contactos anteriores de esa función: los recordatorios de cobranza se envían solo a este.",
          )}
        </p>

        {formError && (
          <p role="alert" className="text-sm text-status-error">
            {tr(formError)}
          </p>
        )}
      </form>
    </Modal>
  );
}
